import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { DynamicDialogModule, DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// API imports
import { Api } from '../../../api/api';
import {
  adminProjectOwnersControllerGetProjectOwnerById,
  adminProjectOwnersControllerCreateProjectOwner,
  adminProjectOwnersControllerUpdateProjectOwner,
} from '../../../api/functions';

@Component({
  selector: 'app-project-owner-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    MessageModule,
    SkeletonModule,
    ToastModule,
    TableModule,
    DynamicDialogModule,
  ],
  providers: [DialogService, MessageService],
  templateUrl: './project-owner-edit.component.html',
  styleUrl: './project-owner-edit.component.css',
})
export class ProjectOwnerEditComponent implements OnInit, OnDestroy {
  private readonly api = inject(Api);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);

  // Loading states
  protected readonly loading = signal(true);
  protected readonly submitting = signal(false);

  protected userId: string | null = null;
  protected isEditMode = false;
  private ref: DynamicDialogRef | undefined;

  // Projects owned by this user
  protected ownerProjects = signal<Array<{ id: string; name: string; status: string }>>([]);

  // Define individual form controls
  protected emailControl = new FormControl('', [Validators.required, Validators.email]);
  protected fullNameControl = new FormControl('');
  protected companyControl = new FormControl('');
  protected passwordControl = new FormControl('', [Validators.required, Validators.minLength(8)]);

  // Create form group using the individual controls
  protected userForm!: FormGroup;

  constructor() {}

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = this.userId !== null && this.userId !== 'new';

    if (this.isEditMode) {
      // Edit mode - without password
      this.userForm = new FormGroup({
        email: this.emailControl,
        fullName: this.fullNameControl,
        company: this.companyControl,
      });
      if (this.userId) {
        this.loadProjectOwner(this.userId);
      }
    } else {
      // Create mode - with password
      this.userForm = new FormGroup({
        email: this.emailControl,
        fullName: this.fullNameControl,
        company: this.companyControl,
        password: this.passwordControl,
      });
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void {
    // No cleanup needed
  }

  private loadProjectOwner(userId: string): void {
    this.loading.set(true);

    // Load project owner with projects included
    this.api
      .invoke(adminProjectOwnersControllerGetProjectOwnerById, {
        id: userId,
        includeProjects: 'true',
      })
      .then((projectOwner) => {
        this.userForm.patchValue({
          email: projectOwner.email,
          fullName: projectOwner.fullName || '',
          company: projectOwner.company || '',
        });

        // Set projects if available
        if (projectOwner.projects) {
          this.ownerProjects.set(projectOwner.projects);
        }

        this.loading.set(false);
      })
      .catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('projectOwnerEdit.error.loadTitle'),
          detail: this.translateService.instant('projectOwnerEdit.error.loadMessage'),
          life: 4000,
        });
        this.loading.set(false);
        this.router.navigate(['/project-owners']);
      });
  }

  protected onSubmit(): void {
    if (this.userForm.valid && !this.submitting()) {
      this.submitting.set(true);
      const formValue = this.userForm.value;

      if (this.isEditMode && this.userId) {
        // Update existing project owner
        this.api
          .invoke(adminProjectOwnersControllerUpdateProjectOwner, {
            id: this.userId,
            body: {
              email: formValue.email!,
              fullName: formValue.fullName || undefined,
              company: formValue.company || undefined,
            },
          })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('projectOwnerEdit.success.editTitle'),
              detail: this.translateService.instant('projectOwnerEdit.success.editMessage'),
              life: 4000,
            });
            this.submitting.set(false);
            this.router.navigate(['/project-owners']);
          })
          .catch(() => {
            this.submitting.set(false);
          });
      } else {
        // Create new project owner
        this.api
          .invoke(adminProjectOwnersControllerCreateProjectOwner, {
            body: {
              email: formValue.email!,
              password: formValue.password!,
              fullName: formValue.fullName || undefined,
              company: formValue.company || undefined,
            },
          })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('projectOwnerEdit.success.createTitle'),
              detail: this.translateService.instant('projectOwnerEdit.success.createMessage'),
              life: 4000,
            });
            this.submitting.set(false);
            this.router.navigate(['/project-owners']);
          })
          .catch(() => {
            this.submitting.set(false);
          });
      }
    } else if (!this.userForm.valid) {
      this.markFormGroupTouched();
    }
  }

  protected onCancel(): void {
    this.router.navigate(['/project-owners']);
  }

  /**
   * Navigate to project details page
   */
  protected openProjectDetails(projectId: string): void {
    this.router.navigate(['/projects', projectId]);
  }

  protected getEmailErrorMessage(): string {
    if (this.emailControl.errors?.['required']) {
      return this.translateService.instant('projectOwnerEdit.errors.emailRequired');
    }
    if (this.emailControl.errors?.['email']) {
      return this.translateService.instant('projectOwnerEdit.errors.emailInvalid');
    }
    return '';
  }

  protected getPasswordErrorMessage(): string {
    if (this.passwordControl.errors?.['required']) {
      return this.translateService.instant('projectOwnerEdit.errors.passwordRequired');
    }
    if (this.passwordControl.errors?.['minlength']) {
      return this.translateService.instant('projectOwnerEdit.errors.passwordMinLength');
    }
    return '';
  }

  private markFormGroupTouched(): void {
    Object.values(this.userForm.controls).forEach((control) => {
      control.markAsTouched();
    });
  }
}
